// ─────────────────────────────────────────────────────────────────────────────
// Shared product state — admin changes propagate to customer & rep portals.
// ─────────────────────────────────────────────────────────────────────────────
//
// This module is a small in-memory pub/sub store for the currently-active
// product + kit catalog. It is NOT a source of truth.
//
// ────── Source-of-truth rules (read this before changing anything) ──────
//
//   • Supabase `portal_products` row (id=1) is the ONLY source of truth for
//     the live catalog. `src/App.js` (AdminPortal) loads it on mount, writes
//     it back whenever the admin hits Save, and pushes the resulting arrays
//     into this module via `setProducts` / `setKits`.
//
//   • localStorage under LS_KEY is a *display cache* so that a brand-new
//     tab can paint the last-seen catalog before the Supabase round-trip
//     completes. It is never treated as newer than Supabase.
//
//   • The old `init(products)` / `initKits(kits)` entry points merged
//     hardcoded seed arrays with localStorage and silently overwrote the
//     in-memory store. That was the root cause of admin edits disappearing
//     on every deploy — a fresh page load would seed from hardcoded
//     defaults, the debounced save in App.js would then push those
//     defaults to Supabase, deleting the admin's real data. Those methods
//     are now no-ops for backward compat.
//
//   • `getSavedAdminProducts` / `getSavedAdminKits` now return `null`. The
//     admin panel must load from Supabase via `getPortalProducts`, never
//     from localStorage. Anything still calling the getters will see the
//     Supabase-hydrated arrays (once the admin mounts), not a stale cache.
//
// ─────────────────────────────────────────────────────────────────────────────

const LS_KEY = "mpcare_shared_products";

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    /* ignore — cache is a nice-to-have */
  }
  return null;
}

function saveToStorage(products, kits) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({
      products: (products || []).map(p => ({ ...p, active: p.active !== false })),
      kits:     (kits     || []).map(k => ({ ...k, active: k.active !== false })),
      savedAt:  new Date().toISOString(),
    }));
  } catch (e) {
    /* ignore */
  }
}

const SHARED_PRODUCTS = {
  _products: [],
  _kits: [],
  _listeners: [],

  // DEPRECATED — left as a no-op so old call sites (`SHARED_PRODUCTS.init(...)`)
  // don't crash. Catalog data now comes from Supabase via setProducts below.
  // eslint-disable-next-line no-unused-vars
  init(_products) { /* intentionally empty */ },
  // eslint-disable-next-line no-unused-vars
  initKits(_kits) { /* intentionally empty */ },

  // Rep and customer portals can opportunistically warm their read-only view
  // from the cache on mount, without claiming it is authoritative. They still
  // subscribe and will be updated the moment the admin's Supabase load
  // completes and calls setProducts/setKits.
  hydrateFromCache() {
    if (this._products.length > 0 || this._kits.length > 0) return;
    const cached = loadFromStorage();
    if (!cached) return;
    if (Array.isArray(cached.products) && cached.products.length > 0) {
      this._products = [...cached.products];
    }
    if (Array.isArray(cached.kits) && cached.kits.length > 0) {
      this._kits = [...cached.kits];
    }
  },

  setProducts(prods) {
    this._products = Array.isArray(prods) ? [...prods] : [];
    this._persist();
    this._notify();
  },
  setKits(kits) {
    this._kits = Array.isArray(kits) ? [...kits] : [];
    this._persist();
    this._notify();
  },

  getActive()     { return this._products.filter(p => p.active !== false); },
  getActiveKits() { return this._kits.filter(k => k.active !== false); },
  getAll()        { return [...this._products]; },
  getAllKits()    { return [...this._kits]; },

  // DEPRECATED — always returns null so no caller can treat localStorage as
  // authoritative state. Use getPortalProducts from ./supabase in admin code.
  getSavedAdminProducts() { return null; },
  getSavedAdminKits()     { return null; },

  _persist() { saveToStorage(this._products, this._kits); },

  subscribe(fn) {
    this._listeners.push(fn);
    return () => { this._listeners = this._listeners.filter(l => l !== fn); };
  },
  _notify() {
    this._listeners.forEach(fn => {
      try { fn({ products: this.getActive(), kits: this.getActiveKits() }); }
      catch (e) { /* listener errors must not break siblings */ }
    });
  },
};

// Warm the in-memory store from the last-known cache at module load. Nothing
// here mutates Supabase — this is purely so a fresh tab can show *something*
// while the admin's getPortalProducts() is still in flight. The admin will
// immediately overwrite these with the authoritative Supabase data.
SHARED_PRODUCTS.hydrateFromCache();

export default SHARED_PRODUCTS;
